import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowsLeftRight, HandArrowDown, HandGrabbing, ArrowCounterClockwise, UserSwitch, CaretDown, CheckCircle, UsersThree } from "@phosphor-icons/react";
import { useI18n } from "@/contexts/I18nContext";

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const B = ({ children }) => <span className="font-bold text-[#000000]">{children}</span>;

const line = (e) => {
  const actor = e.actor_name || "Someone";
  const cls = e.class_name || "a class";
  switch (e.action) {
    case "class.take_over":
      return <><B>{actor}</B> started covering <B>{cls}</B></>;
    case "class.release":
      return <><B>{actor}</B> released the cover on <B>{cls}</B></>;
    case "class.claim":
      return <><B>{actor}</B> permanently claimed <B>{cls}</B>{e.from_teacher_name ? <> from <B>{e.from_teacher_name}</B></> : null}</>;
    case "class.claim_undo":
      return <><B>{actor}</B> undid the claim on <B>{cls}</B>{e.restored_teacher_name ? <> — back to <B>{e.restored_teacher_name}</B></> : null}</>;
    case "class.reassign":
      return <><B>{actor}</B> reassigned <B>{cls}</B>{e.to_teacher_name ? <> to <B>{e.to_teacher_name}</B></> : null}</>;
    case "class.reassign_all":
      return <><B>{actor}</B> handed {e.class_count ? `all ${e.class_count} classes` : "all classes"}{e.from_teacher_name ? <> from <B>{e.from_teacher_name}</B></> : null}{e.to_teacher_name ? <> to <B>{e.to_teacher_name}</B></> : null}</>;
    case "class.pass_all":
      return <><B>{actor}</B> bulk-passed every lesson for <B>{cls}</B>{e.student_count ? ` (${e.student_count} student${e.student_count === 1 ? "" : "s"})` : ""}</>;
    case "class.restore_all":
      return <><B>{actor}</B> undid the bulk-pass on <B>{cls}</B>{e.student_count ? ` (${e.student_count} student${e.student_count === 1 ? "" : "s"} restored)` : ""}</>;
    case "class.share":
      return <><B>{actor}</B> shared <B>{cls}</B>{e.to_teacher_name ? <> with co-teacher <B>{e.to_teacher_name}</B></> : null}</>;
    case "class.unshare":
      return <><B>{actor}</B> stopped sharing <B>{cls}</B>{e.to_teacher_name ? <> with <B>{e.to_teacher_name}</B></> : null}</>;
    case "school.pass_all":
      return <><B>{actor}</B> bulk-passed the whole school <B>{e.school_name || "—"}</B>{e.class_count ? ` (${e.class_count} class${e.class_count === 1 ? "" : "es"}` : ""}{e.student_count ? `${e.class_count ? ", " : " ("}${e.student_count} student${e.student_count === 1 ? "" : "s"})` : (e.class_count ? ")" : "")}</>;
    case "school.restore_all":
      return <><B>{actor}</B> undid the school-wide bulk-pass at <B>{e.school_name || "—"}</B>{e.student_count ? ` (${e.student_count} student${e.student_count === 1 ? "" : "s"} restored)` : ""}</>;
    default:
      return <><B>{actor}</B> — {e.action}</>;
  }
};

const ICONS = {
  "class.take_over": [ArrowsLeftRight, "#1E40AF", "#DBEAFE"],
  "class.release": [HandArrowDown, "#166534", "#DCFCE7"],
  "class.claim": [HandGrabbing, "#92400E", "#FEF3C7"],
  "class.claim_undo": [ArrowCounterClockwise, "#7C2D12", "#FFEDD5"],
  "class.reassign": [UserSwitch, "#3F6212", "#ECFCCB"],
  "class.reassign_all": [UserSwitch, "#3F6212", "#ECFCCB"],
  "class.pass_all": [CheckCircle, "#166534", "#DCFCE7"],
  "class.restore_all": [ArrowCounterClockwise, "#9A3412", "#FFEDD5"],
  "class.share": [UsersThree, "#3F6212", "#F7FEE7"],
  "class.unshare": [UsersThree, "#52525B", "#F4F4F5"],
  "school.pass_all": [CheckCircle, "#0A0D14", "#CCFF00"],
  "school.restore_all": [ArrowCounterClockwise, "#9A3412", "#FFEDD5"],
};

export const SchoolActivityCard = () => {
  const { t } = useI18n();
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(() => localStorage.getItem("schoolActivityOpen") === "1");
  const toggle = () => setOpen((o) => { localStorage.setItem("schoolActivityOpen", o ? "0" : "1"); return !o; });
  useEffect(() => {
    api.get("/classes/activity").then((r) => setEvents(r.data)).catch(() => setEvents(null));
  }, []);
  if (!events || events.length === 0) return null;
  return (
    <div className="card mb-8 py-3" data-testid="school-activity-card">
      <button data-testid="school-activity-toggle" onClick={toggle}
        className="w-full flex items-center justify-between gap-2 bg-transparent border-0 cursor-pointer text-left">
        <span className="flex items-center gap-2 min-w-0">
          <UserSwitch size={18} weight="duotone" color="#1E40AF" />
          <span className="font-bold font-display">{t("School activity — class handoffs")}</span>
          <span className="text-xs text-[#000000] shrink-0">{events.length} {t("recent")}</span>
        </span>
        <CaretDown size={16} weight="bold" className="shrink-0 transition-transform" style={{ transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && (
        <div className="flex flex-col divide-y divide-[#F1F1F3] mt-2" data-testid="school-activity-list">
          {events.map((e) => {
            const [Icon, color, bg] = ICONS[e.action] || [UserSwitch, "#52525B", "#F4F4F5"];
            return (
              <div key={e.id} className="flex items-center gap-3 py-2" data-testid={`activity-row-${e.id}`}>
                <div className="w-7 h-7 rounded-full grid place-items-center shrink-0" style={{ background: bg }}>
                  <Icon size={14} weight="bold" color={color} />
                </div>
                <div className="text-sm text-[#000000] min-w-0 flex-1">{line(e)}</div>
                <div className="text-xs text-[#000000] shrink-0" title={e.at}>{ago(e.at)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
